<template>
    <div class="flex flex-column md:p-4">
        <span class="H6-Medium align-self-center justify-items-center">Class Reschedule Request</span>

        <div class="flex flex-wrap justify-content-between align-items-center gap-2 mt-3 py-2">
            <div class="flex justify-content-start align-items-center gap-2">
                <div v-if="initialLoadingComplete" class="flex justify-center md:justify-start align-items-center">
                    <img :src="profileImageUrl" alt="Profile Image" class="profile-image" />
                </div>
                <div class="flex flex-column justify-content-start gap-2">
                    <span class="text-left Paragraph-Medium">{{ request.student }}</span>
                    <span class="Caption">{{ cleanupLables(request.grade) }}</span>
                    <span class="Caption">{{ classModelText }}</span>
                </div>
            </div>
            <div class="flex align-items-center gap-2">
                <ChatButton v-if="studentId && studentName" :otherUserId="studentId" :otherUserName="studentName" @open-chat="$emit('open-chat', $event)" />
                <Button label="View Profile" class="teal-button-small" @click="viewProfile" />
            </div>
        </div>

        <Divider class="w-full" />

        <div class="flex flex-column gap-4 py-4">
            <div class="grid m-0">

                <div class="col-12 md:col-6 flex flex-column gap-3">
                    <span class="Label">
                        <span class="Paragraph-Medium pr-2">Grade -</span>
                        {{ requestData ? cleanupLables(requestData.classId.grade) : 'Loading...' }}
                    </span>
                    <span class="Label">
                        <span class="Paragraph-Medium pr-2">Subject -</span>
                        {{ requestData ? cleanupLables(requestData.classId.subject) : 'Loading...' }}
                    </span>
                    <span class="Label">
                        <span class="Paragraph-Medium pr-2">Language -</span>
                        {{ requestData ? cleanupLables(requestData.classId.language) : 'Loading...' }}
                    </span>
                    <span class="Label" v-if="requestData && requestData.classId.classType">
                        <span class="Paragraph-Medium pr-2">Class Type -</span>
                        {{ cleanupLables(requestData.classId.classType) }}
                    </span>
                    <span class="Label">
                        <span class="Paragraph-Medium pr-2">Class Mode -</span>
                        {{ requestData ? cleanupLables(requestData.classId.classModel) : 'Loading...' }}
                    </span>
                </div>

                <div class="col-12 md:col-6 flex flex-column gap-3">
                    <span class="Label">
                        <span class="Paragraph-Medium pr-2">Current Date -</span>
                        {{ requestData ? formatToDisplayDate(requestData.classInstanceId.date) : 'Loading...' }}
                    </span>
                    <span class="Label">
                        <span class="Paragraph-Medium pr-2">Current Time -</span>
                        {{ requestData ? formatToDisplayTime(requestData.classInstanceId.start) + ' - ' + formatToDisplayTime(requestData.classInstanceId.end) : 'Loading...' }}
                    </span>
                    <span class="Label">
                        <span class="Paragraph-Medium pr-2">Reschedule Date -</span>
                        {{ requestData ? formatToDisplayDate(requestData.date) : 'Loading...' }}
                    </span>
                    <span class="Label">
                        <span class="Paragraph-Medium pr-2">Reschedule Time -</span>
                        {{ requestData ? formatToDisplayTime(requestData.start) + ' - ' + formatToDisplayTime(requestData.end) : 'Loading...' }}
                    </span>
                    <!-- <span class="Label">
                        <span class="Paragraph-Medium pr-2">Reschedule Scope -</span>
                        {{ requestData ? (requestData.type === 'single' ? 'Only this class' : 'All upcoming classes') : 'Loading...' }}
                    </span> -->
                    <span class="Label">
                        <span class="Paragraph-Medium pr-2">Reschedule Scope -</span>
                        <span class="Paragraph-Medium">
                        {{ requestData ? (requestData.type === 'single' ? 'Only this class' : 'All upcoming classes') : 'Loading...' }}
                        </span>
                    </span>
                </div>

                <div class="col-12 md:col-6 flex flex-column gap-3">
                    <div v-if="requestData && requestData.classId.otherStudents?.length > 0"
                        class="flex align-items-start gap-2">
                        <span class="Paragraph-Medium white-space-nowrap">Other Students -</span>
                        <div class="flex flex-column gap-1">
                            <div v-for="(student, index) in requestData.classId.otherStudents" :key="index"
                                class="Label">
                                {{ student }}
                            </div>
                        </div>
                    </div>

                    <div v-if="requestData && requestData.classId.areasToImprove" class="Label text-justify">
                        <span class="Paragraph-Medium">Specific areas want to improve - </span>
                        {{ requestData.classId.areasToImprove }}
                    </div>

                    <div v-if="requestData && requestData.classId.note" class="Label text-justify">
                        <span class="Paragraph-Medium">Note - </span>
                        {{ requestData.classId.note }}
                    </div>
                </div>
            </div>
        </div>

        <div class="flex flex-wrap justify-content-start gap-3 pb-4">
            <Button label="Reschedule Class" class="teal-button-small" @click="handleAccept" :loading="loading"/>
            <Button label="Reject Request" class="p-button-outlined p-button-danger" @click="handleReject" :loading="loading"/>
        </div>
        <ConfirmDialog></ConfirmDialog>
    </div>
</template>

<script setup>
import { generateSignedUrl } from '@/api/storage';
import blankProfile from '@/assets/blank-profile.png';
import { cleanupLables } from "@/utils/cleanups";
import { formatToDisplayDate, formatToDisplayTime } from "@/utils/cleanups";
// import Avatar from "primevue/avatar";
import Button from "primevue/button";
import ConfirmDialog from "primevue/confirmdialog";
import Divider from "primevue/divider";
import { computed, defineEmits, defineProps, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { getRescheduleRequestDetails, rejectRescheduleRequest } from "@/api/tutor";
import { useConfirm } from "primevue/useconfirm";
import { notifySuccess, notifyError } from "@/utils/notification";
import ChatButton from "@/components/ChatButton.vue";

const props = defineProps({
    request: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['back', 'request-handled', 'reschedule-class', 'open-chat']);
const router = useRouter();

const loading = ref(false);
const confirm = useConfirm();
// const imageUrls = ref({});
const initialLoadingComplete = ref(false);
const profileImageUrl = ref(blankProfile);
const requestData = ref(null);

// State
const requestDetails = ref({
    id: '',
    studentName: '',
    profilePhotoURL: '',
    grade: '',
    subject: '',
    classModel: '',
    reason: '',
    requestedDate: '', // YYYY-MM-DD
    requestedTime: ''  // HH:MM
});

const form = reactive({
    date: '',
    time: ''
});

const studentId = computed(() => props.request.studentId?._id || props.request.studentId);
const studentName = computed(() => props.request.student || props.request.studentName);

const classModelText = computed(() => {
    const model = props.request?.classModel || requestDetails.value.classModel;
    if (!model) return 'Loading...';
    if (model === 'individual_class') return 'Individual Class';
    if (model === 'group_class') return 'Group Class';
    return cleanupLables(model);
});

const viewProfile = () => {
    const id = props.request.studentId?._id || props.request.studentId;
    if (id) {
        router.push(`/studentProfile/${id}`);
    }
};

const initData = async () => {
    if (props.request) {
        const data = props.request;
        requestDetails.value = {
            id: data.id || data._id,
            studentName: data.student || data.studentName,
            profilePhotoURL: data.profilePhotoURL,
            grade: data.grade,
            subject: data.subject,
            classModel: data.classModel,
            reason: data.reason,
            requestedDate: data.date || data.requestedDate,
            requestedTime: data.time || data.requestedTime
        };
        form.date = requestDetails.value.requestedDate;
        form.time = requestDetails.value.requestedTime;

        if (data.classId && typeof data.classId === 'object') {
            requestData.value = data;
        }

        const requestId = data._id || data.id;
        if (requestId) {
            try {
                const res = await getRescheduleRequestDetails(requestId);
                requestData.value = res.data.data;
            } catch (error) {
                console.error("Error fetching request details:", error);
            }
        }

        if (data.profilePhotoURL) {
            try {
                profileImageUrl.value = await generateSignedUrl(data.profilePhotoURL);
            } catch {
                profileImageUrl.value = blankProfile;
            }
        }
        initialLoadingComplete.value = true;
    }
};

watch(() => props.request, initData, { immediate: true });

const handleAccept = async () => {
    if (requestData.value) {
        emit('reschedule-class', requestData.value);
    }
};

const handleReject = () => {
    confirm.require({
        message: 'Are you sure you want to reject this request?',
        header: 'Reject Request',
        icon: 'pi pi-info-circle',
        rejectLabel: 'Cancel',
        acceptLabel: 'Reject',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: async () => {
            loading.value = true;
            try {
                await rejectRescheduleRequest(requestDetails.value.id);
                notifySuccess('Success', 'Request rejected successfully');
                emit('request-handled');
            } catch (error) {
                console.error(error);
                notifyError('Error', 'Failed to reject request');
            } finally {
                loading.value = false;
            }
        }
    });
};
</script>

<style scoped>
.p-button-danger {
    padding: 5px 16px 5px 16px;
    font-size: 14px;
}

.border-right-1 {
    border-right: 1px solid var(--surface-border);
}

@media screen and (max-width: 768px) {
    .border-right-1 {
        border-right: none;
        border-bottom: 1px solid var(--surface-border);
        padding-bottom: 1rem;
        margin-bottom: 1rem;
    }
}
</style>
